// Manifest v2.1 ownership + dependency-graph analyzer —
// tickets/spec-app-dependencies.md §3/§4/§5.
//
// Pure functions over an array of Manifest v2.1 app manifests
// ({ id, collections: [{ key, stateKey, access }], dependencies: [{ id,
// requirement, kind, provides, reason }] }). No network, no filesystem —
// every consumer (the CI derivation test, a future admin diagnostics page)
// passes in the manifests it already has.
//
// THE GROUND TRUTH THIS TRUSTS: `collections[].access` ('own' | 'read' |
// 'own-admin', default 'own' per §3). 'own-admin' (decided 2026-09-30) is an
// OWNER whose collection only admins write — a settings collection such as
// crm_settings: its grant is the 'read' grant ({ read: member, write: admin },
// collectionGrantsOf.js), but here it owns the collection like 'own' (a reader
// depends on its app; it is never an orphan). Building that field
// correctly — i.e. verifying
// which app actually WRITES a collection, from the real el.call bridge call
// sites, not from a prose comment — is a one-time-per-change human/agent
// derivation (documented per-app in each config.js's collections comment).
// This module does not re-derive access from source; it checks that the
// DEPENDENCY graph (§4) is INTERNALLY CONSISTENT with what's already
// declared (§3) — the same trust boundary workspaceModuleRef.js's
// diffDeclaredCollections draws between a manifest and a grant snapshot.

// Every collection key an app declares access:'own' (or 'own-admin') for,
// unioned across all manifests. A collection may have MULTIPLE owners
// (co-written collections
// are real — e.g. nat_stock_lines is written by warehouse-app, commerce-app
// AND scheduling-app) — ownership is a Set, not a single id.
export const buildOwnershipMap = (manifests) => {
  const map = {}
  for (const m of manifests || []) {
    for (const c of (m && m.collections) || []) {
      if (!c || !c.key) continue
      const access = c.access || 'own' // §3 "Default when omitted: 'own'"
      // 'own-admin' owns too (members read, admins write — see the header)
      if (access !== 'own' && access !== 'own-admin') continue
      if (!map[c.key]) map[c.key] = new Set()
      map[c.key].add(m.id)
    }
  }
  return map
}

// For ONE app manifest, group its access:'read' collections by owning app —
// { ownerAppId: Set(collectionKeys) } — using a previously-built ownership
// map. Collections with NO owner anywhere are returned separately as
// `orphans` (tickets/spec-app-dependencies.md §5 "report ... anything you
// could not classify with confidence" — an orphan has no app to depend on,
// so it can never resolve into a dependency edge).
export const deriveDataDependencies = (manifest, ownershipMap) => {
  const byOwner = {}
  const orphans = []
  for (const c of (manifest && manifest.collections) || []) {
    if (!c || !c.key) continue
    const access = c.access || 'own'
    if (access !== 'read') continue
    const owners = ownershipMap[c.key]
    if (!owners || owners.size === 0) {
      orphans.push(c.key)
      continue
    }
    for (const ownerId of owners) {
      if (ownerId === manifest.id) continue // guard: a 'read' entry should never self-own
      byOwner[ownerId] = byOwner[ownerId] || new Set()
      byOwner[ownerId].add(c.key)
    }
  }
  return { byOwner, orphans }
}

// Diff ONE app manifest's DECLARED `dependencies[]` (kind:'data' entries)
// against the DERIVED data-dependency graph computed from its own
// collections[].access plus the ownership map — tickets/spec-app-
// dependencies.md §5:
//   "derived ⊄ declared → undeclared dependency (the catalog/commerce bug)"
//   "declared ⊄ derived → stale dependency (blocks an uninstall for no
//   reason)"
// Mirrors workspaceModuleRef.js diffDeclaredCollections' added/stale shape,
// generalized from bare collection keys to (ownerAppId, collectionKey)
// pairs, since a dependency is scoped to a SPECIFIC provider app.
export const diffDataDependencies = (manifest, ownershipMap) => {
  // FA204 / frank trap — self-contained inline copy of deriveDataDependencies
  // (a bare sibling-export call is exactly as unresolvable post-frank.toJSON
  // as a module-local read; the export above stays live for its own real
  // consumers).
  const { byOwner, orphans } = (() => {
    const byOwner = {}
    const orphans = []
    for (const c of (manifest && manifest.collections) || []) {
      if (!c || !c.key) continue
      const access = c.access || 'own'
      if (access !== 'read') continue
      const owners = ownershipMap[c.key]
      if (!owners || owners.size === 0) {
        orphans.push(c.key)
        continue
      }
      for (const ownerId of owners) {
        if (ownerId === manifest.id) continue
        byOwner[ownerId] = byOwner[ownerId] || new Set()
        byOwner[ownerId].add(c.key)
      }
    }
    return { byOwner, orphans }
  })()

  const declaredPairs = new Set()
  for (const dep of (manifest && manifest.dependencies) || []) {
    if (!dep || !Array.isArray(dep.kind) || dep.kind.indexOf('data') === -1) continue
    for (const key of dep.provides || []) declaredPairs.add(dep.id + '\u0000' + key)
  }

  const derivedPairs = new Set()
  for (const ownerId of Object.keys(byOwner)) {
    for (const key of byOwner[ownerId]) derivedPairs.add(ownerId + '\u0000' + key)
  }

  const toPairs = (set) =>
    [...set].map((p) => {
      const idx = p.indexOf('\u0000')
      return { id: p.slice(0, idx), key: p.slice(idx + 1) }
    })

  const undeclared = toPairs(new Set([...derivedPairs].filter((p) => !declaredPairs.has(p))))
  const stale = toPairs(new Set([...declaredPairs].filter((p) => !derivedPairs.has(p))))

  return { undeclared, stale, orphans }
}

// Every collection key with NO owner across the whole manifest set — a
// records-plane collection that is read/declared somewhere but written by
// nobody (tickets/spec-app-dependencies.md §5 "any collection with NO
// owning app (an orphan)"). Distinct from per-app `orphans` above (which
// only covers one app's read-set) — this is the global view.
export const findGlobalOrphans = (manifests) => {
  // FA204 / frank trap — self-contained inline copy of buildOwnershipMap,
  // see diffDataDependencies above.
  const ownershipMap = (() => {
    const map = {}
    for (const m of manifests || []) {
      for (const c of (m && m.collections) || []) {
        if (!c || !c.key) continue
        const access = c.access || 'own'
        if (access !== 'own' && access !== 'own-admin') continue
        if (!map[c.key]) map[c.key] = new Set()
        map[c.key].add(m.id)
      }
    }
    return map
  })()
  const referenced = new Set()
  for (const m of manifests || []) {
    for (const c of (m && m.collections) || []) {
      if (c && c.key) referenced.add(c.key)
    }
  }
  return [...referenced].filter((key) => !ownershipMap[key] || ownershipMap[key].size === 0).sort()
}

// Detect 2-node cycles in the declared dependency graph: app A depends on
// app B (any requirement) AND app B depends on app A. Resolution at install
// time is transitive (§6) and MUST terminate on a cycle rather than
// recursing — this only reports direct pairs, which is what the twelve
// first-party apps actually have today; a full Tarjan/SCC pass belongs to
// the server's transitive resolver (§6), not this manifest-authoring check.
export const findDependencyCycles = (manifests) => {
  const idsByManifest = {}
  for (const m of manifests || []) {
    idsByManifest[m.id] = new Set((m.dependencies || []).map((d) => d && d.id).filter(Boolean))
  }
  const seen = new Set()
  const cycles = []
  for (const a of Object.keys(idsByManifest)) {
    for (const b of idsByManifest[a]) {
      if (!idsByManifest[b] || !idsByManifest[b].has(a)) continue
      const key = [a, b].sort().join(' <-> ')
      if (seen.has(key)) continue
      seen.add(key)
      cycles.push({ a, b })
    }
  }
  return cycles
}
